import { useCallback, useEffect, useState } from "react";
import {
  CourseHome,
  type LessonBuildStatus
} from "./components/CourseHome";
import { GeneratedLessonPlayer } from "./components/GeneratedLessonPlayer";
import { LessonPlayer } from "./components/LessonPlayer";
import { findLesson, type Lesson } from "./data/curriculum";
import {
  readStoredCreatorToken,
  rememberCreatorToken
} from "./lib/creatorToken";

const activeGenerationStorageKey = "shuni-active-generation-v1";

function routeFromLocation() {
  const search = new URLSearchParams(window.location.search);
  const suppliedToken = search.get("creatorKey") ?? "";
  if (suppliedToken) rememberCreatorToken(suppliedToken);
  if (suppliedToken || search.has("creator")) {
    search.delete("creatorKey");
    search.delete("creator");
    const clean = `${window.location.pathname}?${search.toString()}${window.location.hash}`;
    window.history.replaceState({}, "", clean.replace(/\?$/, ""));
  }
  return {
    creatorToken: suppliedToken || readStoredCreatorToken(),
    lessonId: Number(search.get("lesson")) || null
  };
}

function storedActiveGeneration() {
  const lessonId = Number(localStorage.getItem(activeGenerationStorageKey));
  return Number.isInteger(lessonId) && lessonId > 0 ? lessonId : null;
}

function lessonFromLocation(generatedLessonIds: ReadonlySet<number>) {
  const value = new URLSearchParams(window.location.search).get("lesson");
  if (!value) return null;
  const lesson = findLesson(Number(value));
  if (!lesson) return null;
  if (lesson.pilot) return lesson;
  return generatedLessonIds.has(lesson.id)
    ? {
        ...lesson,
        pilot: true,
        audioMode: "sequence" as const,
        manifestSrc: `/api/lessons/${lesson.id}/manifest`
      }
    : null;
}

async function authorizedJSON(
  path: string,
  token: string,
  init: RequestInit = {}
) {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || "Lesson audio could not be generated.");
  }
  return data;
}

export default function App() {
  const [initialRoute] = useState(routeFromLocation);
  const [creatorToken, setCreatorToken] = useState(initialRoute.creatorToken);
  const [generatedLessonIds, setGeneratedLessonIds] = useState<Set<number>>(
    () => new Set()
  );
  const [buildStatuses, setBuildStatuses] = useState<
    Record<number, LessonBuildStatus>
  >({});
  const [activeGenerationId, setActiveGenerationId] = useState<number | null>(
    storedActiveGeneration
  );
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(() => {
    const lesson = findLesson(initialRoute.lessonId ?? 0);
    return lesson?.pilot ? lesson : null;
  });

  const refreshCatalog = useCallback(async () => {
    const response = await fetch("/api/catalog");
    const data = response.ok ? await response.json() : { generated: [] };
    const ids = new Set<number>(
      (data.generated ?? []).map((entry: { id: number }) => entry.id)
    );
    setGeneratedLessonIds(ids);
    const locationLesson = lessonFromLocation(ids);
    if (locationLesson) setActiveLesson(locationLesson);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/catalog", { signal: controller.signal })
      .then(response => (response.ok ? response.json() : { generated: [] }))
      .then(data => {
        const ids = new Set<number>(
          (data.generated ?? []).map((entry: { id: number }) => entry.id)
        );
        setGeneratedLessonIds(ids);
        const locationLesson = lessonFromLocation(ids);
        if (locationLesson) setActiveLesson(locationLesson);
      })
      .catch(() => {
        // Static pilot lessons still work when the catalog is unavailable.
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setActiveLesson(lessonFromLocation(generatedLessonIds));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [generatedLessonIds]);

  useEffect(() => {
    if (!creatorToken || !activeGenerationId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await authorizedJSON(
          `/api/creator/lesson-status?lessonId=${activeGenerationId}`,
          creatorToken
        );
        if (cancelled) return;
        const job = data.job ?? {};
        const state: LessonBuildStatus["state"] =
          data.generated && data.published
            ? "ready"
            : job.state === "error"
              ? "error"
              : job.state === "rendering"
                ? "rendering"
                : "queued";
        setBuildStatuses(current => ({
          ...current,
          [activeGenerationId]: {
            state,
            completed: job.completed,
            total: job.total,
            message: job.message
          }
        }));

        if (state === "ready") {
          setGeneratedLessonIds(current => new Set(current).add(activeGenerationId));
          setActiveGenerationId(null);
          localStorage.removeItem(activeGenerationStorageKey);
          void refreshCatalog().catch(() => {});
        } else if (state === "error") {
          setActiveGenerationId(null);
          localStorage.removeItem(activeGenerationStorageKey);
        }
      } catch (error) {
        if (cancelled) return;
        setBuildStatuses(current => ({
          ...current,
          [activeGenerationId]: {
            state: "error",
            message:
              error instanceof Error
                ? error.message
                : "Lesson audio could not be generated."
          }
        }));
        setActiveGenerationId(null);
        localStorage.removeItem(activeGenerationStorageKey);
      }
    };

    void poll();
    const timer = window.setInterval(() => void poll(), 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeGenerationId, creatorToken, refreshCatalog]);

  const generateLesson = async (lessonId: number, suppliedToken?: string) => {
    if (activeGenerationId && activeGenerationId !== lessonId) return;
    const token = suppliedToken?.trim() || creatorToken;
    if (!token) return;
    if (suppliedToken) {
      rememberCreatorToken(token);
      setCreatorToken(token);
    }

    setBuildStatuses(current => ({
      ...current,
      [lessonId]: { state: "checking" }
    }));

    try {
      await authorizedJSON("/api/creator/voice-check", token, {
        method: "POST",
        body: "{}"
      });
      await authorizedJSON("/api/creator/generate", token, {
        method: "POST",
        body: JSON.stringify({ lessonId })
      });
      localStorage.setItem(activeGenerationStorageKey, String(lessonId));
      setActiveGenerationId(lessonId);
      setBuildStatuses(current => ({
        ...current,
        [lessonId]: { state: "queued", completed: 0, total: null }
      }));
    } catch (error) {
      setBuildStatuses(current => ({
        ...current,
        [lessonId]: {
          state: "error",
          message:
            error instanceof Error
              ? error.message
              : "Lesson audio could not be generated."
        }
      }));
    }
  };

  const openLesson = (lesson: Lesson) => {
    const url = new URL(window.location.href);
    url.searchParams.set("lesson", String(lesson.id));
    window.history.pushState({}, "", url);
    setActiveLesson(lesson);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const closeLesson = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("lesson");
    window.history.pushState({}, "", url);
    setActiveLesson(null);
  };

  return activeLesson?.audioMode === "sequence" ? (
    <GeneratedLessonPlayer lessonId={activeLesson.id} onClose={closeLesson} />
  ) : activeLesson ? (
    <LessonPlayer lesson={activeLesson} onClose={closeLesson} />
  ) : (
    <CourseHome
      onOpenLesson={openLesson}
      generatedLessonIds={generatedLessonIds}
      buildStatuses={buildStatuses}
      hasGenerationAccess={Boolean(creatorToken)}
      activeGenerationId={activeGenerationId}
      onGenerateLesson={generateLesson}
    />
  );
}
